import React, { useState } from 'react';
import { RolesService } from '../../services/roles.service';
import { IRole } from '../../types/roles.types';
import { PencilIcon, TrashIcon, ShieldCheckIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import RoleModal from './RoleModal';
import { useVault } from '../../contexts/VaultContext';
import ConfirmModal from '../common/ConfirmModal';
import { useAppSelector } from '../../hooks/useRedux';
import { hasPermission } from '../../utils/permissionUtils';

interface RoleListProps {
    setSelectedRole: (role: IRole | null) => void;
    setIsModalOpen: (isOpen: boolean) => void;
    roles: IRole[];
    loading: boolean;
    error: string | null;
    setRoles: (roles: IRole[]) => void;
}

const RoleList: React.FC<RoleListProps> = ({ setSelectedRole, setIsModalOpen, roles, loading, error, setRoles }) => {
    const { searchQuery } = useVault();
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { permissions, role_id } = useAppSelector(state => state.auth);


    const filteredRoles = roles?.filter((role: IRole) => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        return (
            role.name?.toLowerCase().includes(searchLower) ||
            role.description?.toLowerCase().includes(searchLower) ||
            Object.values(role.permissions).some(permission =>
                Object.keys(permission).some(key =>
                    key.toLowerCase().includes(searchLower)
                )
            )
        );
    });

    const handleEdit = (role: IRole) => {
        setSelectedRole(role);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (id: number) => {
        setRoleToDelete(id);
        setShowConfirmModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!roleToDelete) return;

        try {
            setIsDeleting(true);
            const response = await RolesService.deleteRole(roleToDelete);
            if (response?.success) {
                setRoles(roles.filter(role => role.id !== roleToDelete));
                toast.success(response.message || 'Role deleted successfully');
            } else {
                toast.error(response?.message || 'Failed to delete role');
            }
        } catch (err) {
            const errorMessage = (err as any).response?.data?.message || 'Failed to delete role';
            toast.error(errorMessage);
        } finally {
            setIsDeleting(false);
            setShowConfirmModal(false);
            setRoleToDelete(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 shadow-sm">
                <p className="text-red-700 font-medium">Error loading roles: {error}</p>
            </div>
        );
    }

    if (roles.length === 0) {
        return (
            <div className="text-center py-16 px-6">
                <div className="mx-auto w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 
                          rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    <ShieldCheckIcon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">No roles found</h3>
                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                    Get started by creating your first role to define user permissions and access levels.
                </p>
                {hasPermission(permissions, 'role_management', 'create_roles') && (
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                    >
                        <ShieldCheckIcon className="w-5 h-5 mr-2" />
                        Create your first role
                    </button>
                )}
            </div>
        );
    }

    // Format permission name for display
    const formatPermissionName = (permissionKey: string) => {
        return permissionKey.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    };

    // Helper function to get all active permissions
    const getActivePermissions = (permissions: any) => {
        const activePermissions: string[] = [];
        Object.keys(permissions).forEach(category => {
            if (typeof permissions[category] === 'object') {
                Object.entries(permissions[category]).forEach(([key, value]) => {
                    if (value === true) {
                        activePermissions.push(key);
                    }
                });
            }
        });
        return activePermissions;
    };

    // Helper function to check if role has all permissions
    const hasAllPermissions = (permissions: any) => {
        // Define expected permissions for each category
        const expectedPermissions = {
            team: [
                'view_teams',
                'create_teams',
                'update_teams',
                'delete_teams',
                'invite_members',
                'remove_members',
                'view_team_members'
            ],
            role_management: [
                'view_roles',
                'create_roles',
                'edit_roles',
                'delete_roles',
                'assign_roles'
            ],
            vault: [
                'vault_share_teams',
                'export_items'
            ]
        };

        // Check each category
        for (const [category, permissionList] of Object.entries(expectedPermissions)) {
            if (!permissions[category]) return false;

            // Check each permission in the category
            for (const permission of permissionList) {
                if (!permissions[category][permission]) {
                    return false;
                }
            }
        }

        return true;
    };

    return (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-3">
            {/* Desktop view - Traditional table */}
            <div className="hidden md:block">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Role
                            </th>

                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Permissions
                            </th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredRoles?.filter(role => role.id !== 1).map(role => (
                            <tr key={role.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                                            {role.name.toLowerCase().includes('admin') ? (
                                                <ShieldCheckIcon className="h-6 w-6 text-purple-600" />
                                            ) : (
                                                <UserGroupIcon className="h-6 w-6 text-blue-600" />
                                            )}
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900">{role.name}</div>
                                            <div className="text-xs text-gray-500 max-w-xs truncate">{role.description}</div>
                                        </div>
                                    </div>
                                </td>

                                <td className="px-6 py-4">
                                    <div className="flex flex-wrap gap-1">
                                        {role.id === 1 || hasAllPermissions(role.permissions) ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                All Permissions
                                            </span>
                                        ) : (
                                            getActivePermissions(role.permissions).map((key) => (
                                                <span
                                                    key={key}
                                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 me-2"
                                                >
                                                    {formatPermissionName(key)}
                                                </span>
                                            ))
                                        )}
                                        {role.id !== 1 && !hasAllPermissions(role.permissions) && getActivePermissions(role.permissions).length === 0 && (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                No Permissions
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    {hasPermission(permissions, 'role_management', 'edit_roles') && (
                                        (role.id !== 2 || role_id === 1) && (
                                            <div className="flex items-center justify-end space-x-3">
                                                <button
                                                    onClick={() => handleEdit(role)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="Edit role"
                                                >
                                                    <PencilIcon className="h-5 w-5" />
                                                </button>
                                                {hasPermission(permissions, 'role_management', 'delete_roles') && (
                                                    <button
                                                        onClick={() => handleDeleteClick(role.id)}
                                                        className="text-red-600 hover:text-red-900"
                                                        title="Delete role"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                )}
                                            </div>
                                        )
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile view - Card-based layout */}
            <div className="block md:hidden">
                <ul className="divide-y divide-gray-200">
                    {filteredRoles.filter(role => role.id !== 1).map(role => (
                        <li key={role.id} className="py-4 px-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center">
                                        {role.name.toLowerCase().includes('admin') ? (
                                            <ShieldCheckIcon className="h-7 w-7 text-purple-600" />
                                        ) : (
                                            <UserGroupIcon className="h-7 w-7 text-blue-600" />
                                        )}
                                    </div>
                                    <div className="ml-3">
                                        <div className="flex items-center">
                                            <p className="text-sm font-medium text-gray-900">{role.name}</p>
                                        </div>
                                        <p className="text-xs text-gray-500 max-w-xs truncate">{role.description}</p>
                                    </div>
                                </div>
                                {hasPermission(permissions, 'role_management', 'edit_roles') && (
                                    (role.id !== 2 || role_id === 1) && (

                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handleEdit(role)}
                                                className="p-2 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50"
                                            >
                                                <PencilIcon className="h-5 w-5" />
                                            </button>
                                            {hasPermission(permissions, 'role_management', 'delete_roles') && (
                                                <button
                                                    onClick={() => handleDeleteClick(role.id)}
                                                    className="p-2 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50"
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            )}
                                        </div>
                                    )
                                )}
                            </div>

                            {/* Permissions section */}
                            <div className="mt-3 ml-12">
                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Permissions</p>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {role.id === 1 || hasAllPermissions(role.permissions) ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            All Permissions
                                        </span>
                                    ) : (
                                        getActivePermissions(role.permissions).map((key) => (
                                            <span
                                                key={key}
                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 me-2"
                                            >
                                                {formatPermissionName(key)}
                                            </span>
                                        ))
                                    )}
                                    {role.id !== 1 && !hasAllPermissions(role.permissions) && getActivePermissions(role.permissions).length === 0 && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            No Permissions
                                        </span>
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Confirm Delete Modal */}
            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Role"
                message="Are you sure you want to delete this role? This action cannot be undone."
                confirmText="Delete"
                isLoading={isDeleting}
            />
        </div>
    );
};

export default RoleList; 